import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { useSearchParams, useNavigate } from "react-router-dom";
import { font, Button, Panel, Badge, ProgressBar } from "@realtyflows/ui";
import { useTheme } from "../contexts/ThemeContext";
import useResponsive from "../hooks/useResponsive";
import { getBillingStatus, createCheckout, createBillingPortal, getPaymentMethod, getInvoices, getSubscription, changePlan, cancelSubscription, resumeSubscription } from "../api";
import EsignQuotaMeter from "../components/EsignQuotaMeter";

const planConfig = {
  free: { label: "Free", price: 0, category: "Solo" },
  starter: { label: "Starter", price: 39, category: "Solo" },
  pro: { label: "Pro", price: 89, category: "Solo" },
  team_starter: { label: "Team Starter", price: 249, category: "Team" },
  team_pro: { label: "Team Pro", price: 549, category: "Team" },
  agency: { label: "Agency", price: 999, category: "Team" },
  enterprise: { label: "Enterprise", price: null, category: "Team" },
};

const tierLevel = { free: 0, starter: 1, pro: 2, team_starter: 3, team_pro: 4, agency: 5, enterprise: 6 };

const planFeatures = {
  free: ["1 signing", "1 user", "Unlimited CRM", "Pipeline view", "Email support"],
  starter: ["3 signings/mo", "1 user", "Basic AI", "Pipeline view", "Email support"],
  pro: ["10 signings/mo", "1 user", "Full AI", "Smart automation", "Priority email support"],
  team_starter: ["20 signings/mo", "5 users included", "Full AI", "Team management", "+$29/extra seat"],
  team_pro: ["50 signings/mo", "15 users included", "Advanced AI", "Brokerage pipeline", "+$29/extra seat"],
  agency: ["100 signings/mo", "50 users included", "Advanced AI + White Label", "Custom branding", "+$19/extra seat"],
  enterprise: ["Custom signings", "Custom users", "Custom AI", "API & webhooks", "24/7 support"],
};

const cancelReasons = [
  { value: "too_expensive", label: "Too expensive" },
  { value: "not_using", label: "Not using it enough" },
  { value: "missing_features", label: "Missing features I need" },
  { value: "switching", label: "Switching to another tool" },
  { value: "temporary", label: "Just taking a break" },
  { value: "other", label: "Other" },
];

export default function Billing({ user }) {
  const t = useTheme();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const responsive = useResponsive();
  const tab = searchParams.get("tab") || "payment";

  const [billing, setBilling] = useState(null);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState("");
  const [paymentMethod, setPaymentMethod] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [invoicesLoading, setInvoicesLoading] = useState(false);
  const [subscription, setSubscription] = useState(null);
  const [billingInterval, setBillingInterval] = useState("monthly");
  const [action, setAction] = useState("");
  const [cancelReason, setCancelReason] = useState("");
  const [cancelOther, setCancelOther] = useState("");
  const [cancelStep, setCancelStep] = useState(1);
  const [switchConfirm, setSwitchConfirm] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const b = await getBillingStatus();
        setBilling(b);
        if (b?.hasPaymentMethod || b?.subscriptionStatus === "active") {
          getPaymentMethod().then(r => setPaymentMethod(r.paymentMethod)).catch(() => {});
          getSubscription().then(r => setSubscription(r.subscription)).catch(() => {});
        }
      } catch {
        setBilling(null);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  useEffect(() => {
    if (tab === "invoices" && !invoicesLoading) {
      setInvoicesLoading(true);
      getInvoices().then(r => setInvoices(r.invoices || [])).catch(() => {}).finally(() => setInvoicesLoading(false));
    }
  }, [tab]);

  const handleManageBilling = async () => {
    try {
      const { url } = await createBillingPortal();
      if (url) window.location.href = url;
    } catch (err) {
      setMsg(err.message || "Failed to open billing portal");
    }
  };

  const handleChangePlan = (newPlan) => {
    const cfg = planConfig[newPlan];
    const currentCfg = planConfig[currentPlan];
    const newPrice = cfg.price != null ? (billingInterval === "annual" && cfg.price > 0 ? Math.round(cfg.price * 10 / 12) : cfg.price) : null;
    const oldPrice = currentCfg?.price != null ? (billingInterval === "annual" && currentCfg.price > 0 ? Math.round(currentCfg.price * 10 / 12) : currentCfg.price) : 0;
    const isUpgrade = tierLevel[newPlan] > tierLevel[currentPlan];
    const hasActiveSub = billing?.subscriptionStatus === "active";
    setSwitchConfirm({ plan: newPlan, label: cfg.label, newPrice, oldPrice, isUpgrade, isSwitch: hasActiveSub });
  };

  const handleConfirmSwitch = async () => {
    if (!switchConfirm) return;
    // Downgrade to free = cancel subscription
    if (switchConfirm.plan === "free") {
      setAction("changing");
      try {
        await cancelSubscription("Downgraded to free plan");
        setMsg("Downgraded to Free plan. Your subscription will end at the current billing period.");
        const b = await getBillingStatus();
        setBilling(b);
        setSwitchConfirm(null);
      } catch (err) {
        setMsg(err.message || "Failed to downgrade");
        setSwitchConfirm(null);
      } finally {
        setAction("");
      }
      return;
    }
    setAction("changing");
    try {
      if (!switchConfirm.isSwitch) {
        // No active subscription — go to Stripe Checkout
        setSwitchConfirm(null);
        const { url } = await createCheckout(switchConfirm.plan, billingInterval);
        if (url) window.location.href = url;
        return;
      }
      await changePlan(switchConfirm.plan, billingInterval);
      setMsg(`${switchConfirm.isUpgrade ? "Upgraded" : "Downgraded"} to ${switchConfirm.label}!`);
      const b = await getBillingStatus();
      setBilling(b);
      const s = await getSubscription();
      setSubscription(s.subscription);
      const pm = await getPaymentMethod();
      setPaymentMethod(pm.paymentMethod);
      setSwitchConfirm(null);
    } catch (err) {
      if (err.message?.includes("No active subscription")) {
        try {
          setSwitchConfirm(null);
          const { url } = await createCheckout(switchConfirm.plan, billingInterval);
          if (url) window.location.href = url;
          return;
        } catch (checkoutErr) {
          setMsg(checkoutErr.message || "Failed to start checkout");
        }
      } else {
        setMsg(err.message || "Failed to change plan");
      }
      setSwitchConfirm(null);
    } finally {
      setAction("");
    }
  };

  const handleCancel = async () => {
    setAction("cancelling");
    try {
      const reason = cancelReason === "other" ? cancelOther || "other" : cancelReason;
      await cancelSubscription(reason);
      navigate("/settings?billing=cancelled");
    } catch (err) {
      setMsg(err.message || "Failed to cancel");
    } finally {
      setAction("");
    }
  };

  const handleResume = async () => {
    setAction("resuming");
    try {
      await resumeSubscription();
      setMsg("Subscription resumed successfully!");
      const b = await getBillingStatus();
      setBilling(b);
      const s = await getSubscription();
      setSubscription(s.subscription);
    } catch (err) {
      setMsg(err.message || "Failed to resume");
    } finally {
      setAction("");
    }
  };

  const currentPlan = billing?.plan || "free";
  const currentLevel = tierLevel[currentPlan] ?? 0;
  const config = planConfig[currentPlan] || planConfig.free;
  const isActive = billing?.subscriptionStatus === "active" || billing?.subscriptionStatus === "cancelling";

  const tabs = [
    ...(isActive ? [
      { key: "payment", label: "Payment Method" },
      { key: "invoices", label: "Invoices" },
    ] : []),
    { key: "plans", label: "Plans" },
    ...(isActive && !subscription?.cancelAtPeriodEnd ? [{ key: "cancel", label: "Cancel Plan" }] : []),
  ];

  if (loading) {
    return (
      <div style={{ animation: "fadeSlide 0.3s ease" }}>
        <Panel padding={40} style={{ textAlign: "center" }}>
          <div style={{ fontSize: 13, color: t.textMuted, fontFamily: font.body }}>Loading billing info...</div>
        </Panel>
      </div>
    );
  }

  return (
    <div style={{ animation: "fadeSlide 0.3s ease", maxWidth: 800, margin: "0 auto" }}>
      {/* Back link */}
      <div onClick={() => navigate("/settings")} style={{
        fontSize: 12, color: t.textMuted, cursor: "pointer", marginBottom: 16,
        fontFamily: font.body, display: "flex", alignItems: "center", gap: 6,
      }}>
        <span style={{ fontSize: 14 }}>&larr;</span> Back to Settings
      </div>

      {msg && (
        <div style={{
          background: msg.includes("success") || msg.includes("changed") || msg.includes("resumed") ? t.emeraldLight : t.amberLight,
          border: `1px solid ${msg.includes("success") || msg.includes("changed") || msg.includes("resumed") ? `${t.emerald}40` : `${t.amber}40`}`,
          borderRadius: 10, padding: "12px 16px", marginBottom: 20, fontSize: 13,
          color: msg.includes("success") || msg.includes("changed") || msg.includes("resumed") ? t.emeraldDeep : t.amber,
          display: "flex", justifyContent: "space-between", alignItems: "center", fontFamily: font.body,
        }}>
          <span>{msg}</span>
          <span onClick={() => setMsg("")} style={{ cursor: "pointer", fontWeight: 700, fontSize: 16 }}>&times;</span>
        </div>
      )}

      {/* E-Sign Usage Meter (V1) */}
      <EsignQuotaMeter />

      {/* Current plan summary */}
      <Panel padding={20} style={{ marginBottom: 20 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <div style={{ fontSize: 10, color: t.emerald, fontWeight: 700, letterSpacing: 1, marginBottom: 2, fontFamily: font.mono }}>CURRENT PLAN</div>
            <div style={{ fontSize: 18, fontWeight: 800, color: t.textPrimary, fontFamily: font.display }}>{config.label}</div>
          </div>
          <div style={{ textAlign: "right" }}>
            <div style={{ fontSize: 24, fontWeight: 800, color: t.emerald, fontFamily: font.mono }}>
              ${config.price}<span style={{ fontSize: 12, color: t.textMuted }}>/mo</span>
            </div>
            {isActive && <Badge variant="emerald">Active</Badge>}
          </div>
        </div>
        {subscription?.cancelAtPeriodEnd && (
          <div style={{
            display: "flex", gap: 8, alignItems: "center", marginTop: 12,
          }}>
            <div style={{
              flex: 1, background: t.amberLight, borderRadius: 8, padding: "10px 14px",
              border: `1px solid ${t.amber}30`, fontSize: 12, color: t.amber, fontFamily: font.body,
            }}>
              Cancels on {subscription.currentPeriodEnd ? new Date(subscription.currentPeriodEnd).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" }) : "end of billing period"}
            </div>
            <Button variant="primary" onClick={handleResume} disabled={!!action}>
              {action === "resuming" ? "Resuming..." : "Resume"}
            </Button>
          </div>
        )}
      </Panel>

      {/* Tab navigation */}
      <div style={{
        display: "flex", gap: 0, marginBottom: 20, background: t.pageBg,
        borderRadius: 10, padding: 3, border: `1px solid ${t.border}`,
      }}>
        {tabs.map(tb => (
          <div
            key={tb.key}
            onClick={() => navigate(`/billing?tab=${tb.key}`, { replace: true })}
            style={{
              flex: 1, padding: "10px 0", textAlign: "center", borderRadius: 8,
              fontSize: 12, fontWeight: 600, fontFamily: font.body, cursor: "pointer",
              transition: "all 0.2s",
              background: tab === tb.key ? t.surface : "transparent",
              color: tab === tb.key ? t.textPrimary : t.textMuted,
              boxShadow: tab === tb.key ? "0 1px 3px rgba(0,0,0,0.1)" : "none",
            }}
          >
            {tb.label}
          </div>
        ))}
      </div>

      {/* Tab Content */}
      {tab === "payment" && (
        <Panel padding={20}>
          <div style={{ fontSize: 14, fontWeight: 700, color: t.textPrimary, marginBottom: 16, fontFamily: font.display }}>Payment Method</div>
          {paymentMethod ? (
            <div>
              <div style={{
                display: "flex", alignItems: "center", justifyContent: "space-between",
                background: t.surfaceWarm, borderRadius: 10, padding: "16px 20px", border: `1px solid ${t.border}`,
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                  <div style={{
                    background: t.navyLight, color: "#fff", borderRadius: 6, padding: "6px 12px",
                    fontSize: 11, fontWeight: 700, fontFamily: font.mono, textTransform: "uppercase",
                  }}>{paymentMethod.brand}</div>
                  <div>
                    <div style={{ fontSize: 14, color: t.textPrimary, fontFamily: font.mono, fontWeight: 600 }}>
                      •••• •••• •••• {paymentMethod.last4}
                    </div>
                    <div style={{ fontSize: 11, color: t.textMuted, fontFamily: font.body, marginTop: 2 }}>
                      Expires {paymentMethod.expMonth}/{paymentMethod.expYear}
                    </div>
                  </div>
                </div>
              </div>
              <div style={{ marginTop: 16 }}>
                <Button variant="outline" onClick={handleManageBilling}>Update Payment Method</Button>
              </div>
            </div>
          ) : (
            <div>
              <div style={{ fontSize: 13, color: t.textMuted, fontFamily: font.body, marginBottom: 16 }}>
                No payment method on file.
              </div>
              <Button variant="primary" onClick={handleManageBilling}>Add Payment Method</Button>
            </div>
          )}
        </Panel>
      )}

      {tab === "invoices" && (
        <Panel padding={20}>
          <div style={{ fontSize: 14, fontWeight: 700, color: t.textPrimary, marginBottom: 16, fontFamily: font.display }}>Invoices</div>
          {invoicesLoading ? (
            <div style={{ fontSize: 13, color: t.textMuted, fontFamily: font.body, textAlign: "center", padding: 20 }}>Loading invoices...</div>
          ) : invoices.length === 0 ? (
            <div style={{ fontSize: 13, color: t.textMuted, fontFamily: font.body, textAlign: "center", padding: 20 }}>No invoices yet.</div>
          ) : (
            <div>
              {invoices.map((inv, i) => (
                <div key={inv.id || i} style={{
                  display: "flex", alignItems: "center", justifyContent: "space-between",
                  padding: "12px 0", borderBottom: i < invoices.length - 1 ? `1px solid ${t.border}` : "none",
                }}>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: t.textPrimary, fontFamily: font.body }}>
                      {new Date(inv.date || inv.created * 1000).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                    </div>
                    <div style={{ fontSize: 11, color: t.textMuted, fontFamily: font.body }}>
                      {inv.number || `Invoice #${i + 1}`}
                    </div>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: t.textPrimary, fontFamily: font.mono }}>
                      ${(inv.amount != null ? inv.amount : (inv.amount_paid || 0) / 100).toFixed(2)}
                    </div>
                    <Badge variant={inv.status === "paid" ? "emerald" : "amber"}>
                      {inv.status === "paid" ? "Paid" : inv.status}
                    </Badge>
                    {(inv.hostedUrl || inv.hosted_invoice_url) && (
                      <a href={inv.hostedUrl || inv.hosted_invoice_url} target="_blank" rel="noopener noreferrer" style={{
                        fontSize: 11, color: t.emerald, fontWeight: 600, fontFamily: font.body, textDecoration: "none",
                      }}>View</a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Panel>
      )}

      {tab === "plans" && (
        <Panel padding={20}>
          <div style={{ fontSize: 14, fontWeight: 700, color: t.textPrimary, marginBottom: 16, fontFamily: font.display }}>Change Plan</div>

          {/* Monthly / Annual Toggle */}
          <div style={{ display: "flex", alignItems: "center", gap: 0, marginBottom: 20, background: t.pageBg, borderRadius: 99, padding: 2, border: `1px solid ${t.border}`, maxWidth: 240 }}>
            <button onClick={() => setBillingInterval("monthly")} style={{
              flex: 1, padding: "8px 0", borderRadius: 99, border: "none", cursor: "pointer",
              fontSize: 12, fontWeight: 600, fontFamily: font.body, transition: "all 0.2s",
              background: billingInterval === "monthly" ? t.emerald : "transparent",
              color: billingInterval === "monthly" ? "#fff" : t.textMuted,
            }}>Monthly</button>
            <button onClick={() => setBillingInterval("annual")} style={{
              flex: 1, padding: "8px 0", borderRadius: 99, border: "none", cursor: "pointer",
              fontSize: 12, fontWeight: 600, fontFamily: font.body, transition: "all 0.2s",
              background: billingInterval === "annual" ? t.emerald : "transparent",
              color: billingInterval === "annual" ? "#fff" : t.textMuted,
            }}>Annual <span style={{ fontSize: 9, color: billingInterval === "annual" ? "#fff" : t.emerald }}>Save 2mo</span></button>
          </div>

          {/* Solo Plans */}
          <div style={{ fontSize: 10, fontWeight: 700, color: t.textMuted, marginBottom: 8, fontFamily: font.mono, letterSpacing: 0.5 }}>SOLO AGENT</div>
          <div style={{ display: "grid", gridTemplateColumns: responsive.width < 640 ? "1fr" : "repeat(3, 1fr)", gap: 12, marginBottom: 20 }}>
            {Object.entries(planFeatures).filter(([key]) => ["free", "starter", "pro"].includes(key)).map(([key, features]) => {
              const cfg = planConfig[key];
              const isFree = key === "free";
              const displayPrice = cfg.price != null ? (billingInterval === "annual" && cfg.price > 0 ? Math.round(cfg.price * 10 / 12) : cfg.price) : null;
              const isCurrent = isFree ? (currentPlan === "free" || billing?.isFree) : currentPlan === key;
              const isUpgrade = tierLevel[key] > currentLevel;
              return (
                <div key={key} style={{
                  background: isCurrent ? t.emeraldLight : t.surfaceWarm,
                  border: isCurrent ? `2px solid ${t.emerald}40` : `1px solid ${t.border}`,
                  borderRadius: 10, padding: 16,
                }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: isCurrent ? t.emerald : t.textMuted, marginBottom: 4, fontFamily: font.body }}>
                    {cfg.label}
                  </div>
                  <div style={{ fontSize: 20, fontWeight: 800, color: t.textPrimary, marginBottom: 8, fontFamily: font.mono }}>
                    ${displayPrice}{cfg.price > 0 && <span style={{ fontSize: 11, color: t.textMuted, fontWeight: 400 }}>/mo</span>}
                  </div>
                  {features.map(f => (
                    <div key={f} style={{ fontSize: 11, color: t.textPrimary, marginBottom: 3, display: "flex", gap: 6, alignItems: "flex-start", fontFamily: font.body }}>
                      <span style={{ color: t.emerald, fontSize: 10, marginTop: 1, flexShrink: 0 }}>{"\u2713"}</span> {f}
                    </div>
                  ))}
                  <div style={{ marginTop: 12 }}>
                    {isCurrent ? (
                      <Badge variant="emerald">Current Plan</Badge>
                    ) : (
                      <Button
                        variant={isUpgrade ? "primary" : "outline"}
                        style={{ width: "100%", fontSize: 11 }}
                        onClick={() => handleChangePlan(key)}
                        disabled={!!action}
                      >
                        {action === "changing" ? "..." : isUpgrade ? "Upgrade" : "Downgrade"}
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Team Plans */}
          <div style={{ fontSize: 10, fontWeight: 700, color: t.textMuted, marginBottom: 8, fontFamily: font.mono, letterSpacing: 0.5 }}>TEAM</div>
          <div style={{ display: "grid", gridTemplateColumns: responsive.planColumns, gap: 12 }}>
            {Object.entries(planFeatures).filter(([key]) => ["team_starter", "team_pro", "agency", "enterprise"].includes(key)).map(([key, features]) => {
              const cfg = planConfig[key];
              const displayPrice = cfg.price != null ? (billingInterval === "annual" ? Math.round(cfg.price * 10 / 12) : cfg.price) : null;
              const isCurrent = currentPlan === key;
              const isUpgrade = tierLevel[key] > currentLevel;
              return (
                <div key={key} style={{
                  background: isCurrent ? t.emeraldLight : t.surfaceWarm,
                  border: isCurrent ? `2px solid ${t.emerald}40` : `1px solid ${t.border}`,
                  borderRadius: 10, padding: 16,
                }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: isCurrent ? t.emerald : t.textMuted, marginBottom: 4, fontFamily: font.body }}>
                    {cfg.label}
                  </div>
                  <div style={{ fontSize: 20, fontWeight: 800, color: t.textPrimary, marginBottom: 8, fontFamily: font.mono }}>
                    {displayPrice != null ? (
                      <>${displayPrice.toLocaleString()}<span style={{ fontSize: 11, color: t.textMuted, fontWeight: 400 }}>/mo</span></>
                    ) : (
                      <span style={{ fontSize: 14, fontWeight: 700 }}>Contact Sales</span>
                    )}
                  </div>
                  {features.map(f => (
                    <div key={f} style={{ fontSize: 11, color: t.textPrimary, marginBottom: 3, display: "flex", gap: 6, alignItems: "flex-start", fontFamily: font.body }}>
                      <span style={{ color: t.emerald, fontSize: 10, marginTop: 1, flexShrink: 0 }}>{"\u2713"}</span> {f}
                    </div>
                  ))}
                  <div style={{ marginTop: 12 }}>
                    {isCurrent ? (
                      <Badge variant="emerald">Current Plan</Badge>
                    ) : key === "enterprise" ? (
                      <Button variant="outline" style={{ width: "100%", fontSize: 11 }} onClick={() => navigate("/contact?plan=enterprise")}>Contact Sales</Button>
                    ) : (
                      <Button
                        variant={isUpgrade ? "primary" : "outline"}
                        style={{ width: "100%", fontSize: 11 }}
                        onClick={() => handleChangePlan(key)}
                        disabled={!!action}
                      >
                        {action === "changing" ? "..." : isUpgrade ? "Upgrade" : "Downgrade"}
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </Panel>
      )}

      {tab === "cancel" && (
        <Panel padding={20}>
          <div style={{ fontSize: 14, fontWeight: 700, color: t.textPrimary, marginBottom: 6, fontFamily: font.display }}>Cancel Subscription</div>
          <div style={{ fontSize: 12, color: t.textMuted, marginBottom: 20, fontFamily: font.body }}>
            We're sorry to see you go. Your subscription will remain active until the end of your current billing period.
          </div>

          {cancelStep === 1 ? (
            <div>
              <div style={{ fontSize: 12, fontWeight: 600, color: t.textPrimary, marginBottom: 10, fontFamily: font.body }}>
                Why are you cancelling?
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 20 }}>
                {cancelReasons.map(r => (
                  <label key={r.value} style={{
                    display: "flex", alignItems: "center", gap: 10, padding: "10px 14px",
                    background: cancelReason === r.value ? t.emeraldLight : t.surfaceWarm,
                    border: `1px solid ${cancelReason === r.value ? `${t.emerald}40` : t.border}`,
                    borderRadius: 8, cursor: "pointer", fontSize: 13, fontFamily: font.body,
                    transition: "all 0.15s",
                  }}>
                    <input
                      type="radio"
                      name="cancelReason"
                      value={r.value}
                      checked={cancelReason === r.value}
                      onChange={() => setCancelReason(r.value)}
                      style={{ accentColor: t.emerald }}
                    />
                    {r.label}
                  </label>
                ))}
              </div>
              {cancelReason === "other" && (
                <div style={{ marginBottom: 16 }}>
                  <textarea
                    value={cancelOther}
                    onChange={e => setCancelOther(e.target.value)}
                    placeholder="Tell us more..."
                    style={{
                      width: "100%", minHeight: 80, padding: 12, borderRadius: 8,
                      border: `1px solid ${t.border}`, background: t.surfaceWarm,
                      fontSize: 13, fontFamily: font.body, color: t.textPrimary,
                      resize: "vertical", outline: "none", boxSizing: "border-box",
                    }}
                  />
                </div>
              )}
              <div style={{ display: "flex", gap: 10 }}>
                <Button
                  variant="outline"
                  style={{ borderColor: t.coral, color: t.coral }}
                  onClick={() => setCancelStep(2)}
                  disabled={!cancelReason}
                >
                  Continue to Cancel
                </Button>
                <Button variant="outline" onClick={() => navigate("/billing?tab=payment", { replace: true })}>
                  Never Mind
                </Button>
              </div>
            </div>
          ) : (
            <div>
              <div style={{
                background: t.coralLight, borderRadius: 10, padding: 16, marginBottom: 16,
                border: `1px solid ${t.coral}30`,
              }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: t.coral, marginBottom: 6, fontFamily: font.body }}>
                  Are you sure?
                </div>
                <div style={{ fontSize: 12, color: t.textPrimary, fontFamily: font.body, lineHeight: 1.5, marginBottom: 12 }}>
                  Your plan will remain active until the end of your current billing period.
                  After that, these features will be deactivated:
                </div>
                <div style={{ fontSize: 11, color: t.textSecondary, fontFamily: font.body, lineHeight: 1.8 }}>
                  {(planFeatures[currentPlan] || []).map(f => (
                    <div key={f} style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <span style={{ color: t.coral, fontSize: 10 }}>{"\u2717"}</span> {f}
                    </div>
                  ))}
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <span style={{ color: t.emerald, fontSize: 10 }}>{"\u2713"}</span> Your data (clients, deals, templates) will be kept
                  </div>
                </div>
              </div>
              <div style={{ display: "flex", gap: 10 }}>
                <Button variant="primary" onClick={() => setCancelStep(1)}>
                  Keep My Plan
                </Button>
                <Button
                  variant="outline"
                  style={{ borderColor: t.coral, color: t.coral }}
                  onClick={handleCancel}
                  disabled={!!action}
                >
                  {action === "cancelling" ? "Cancelling..." : "Cancel Anyway"}
                </Button>
              </div>
            </div>
          )}
        </Panel>
      )}
      {switchConfirm && createPortal(
        <div style={{ position: "fixed", top: 0, right: 0, bottom: 0, left: 220, background: "rgba(0,0,0,0.5)", zIndex: 10000, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
          <div style={{ background: t.surface, borderRadius: 16, padding: 28, maxWidth: 420, width: "100%", boxShadow: "0 20px 60px rgba(0,0,0,0.3)" }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: t.textPrimary, marginBottom: 6, fontFamily: font.display }}>
              {switchConfirm.plan === "free" ? "Downgrade to Free" : switchConfirm.isSwitch ? (switchConfirm.isUpgrade ? "Upgrade" : "Downgrade") + " to " + switchConfirm.label : "Subscribe to " + switchConfirm.label}
            </div>
            <div style={{ fontSize: 13, color: t.textSecondary, marginBottom: 20, fontFamily: font.body, lineHeight: 1.6 }}>
              {switchConfirm.plan === "free"
                ? <>Your <strong>{planConfig[currentPlan]?.label}</strong> subscription will be cancelled at the end of your billing period.</>
                : switchConfirm.isSwitch
                  ? <>Switch from <strong>{planConfig[currentPlan]?.label}</strong> to <strong>{switchConfirm.label}</strong></>
                  : <>You're about to subscribe to the <strong>{switchConfirm.label}</strong> plan</>
              }
            </div>

            <div style={{
              background: switchConfirm.plan === "free" ? "#fef2f2" : t.emeraldLight,
              border: `1px solid ${switchConfirm.plan === "free" ? "#fca5a530" : t.emerald + "30"}`,
              borderRadius: 10, padding: 16, marginBottom: 20,
            }}>
              {switchConfirm.plan === "free" ? (
                <div style={{ fontSize: 11, color: t.textSecondary, fontFamily: font.body, lineHeight: 1.6 }}>
                  You'll keep access to your current plan until the end of this billing period. After that, you'll be on the Free plan with unlimited CRM and 1 signing. Your data will be preserved.
                </div>
              ) : (
                <>
                  {switchConfirm.isSwitch && (
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                      <span style={{ fontSize: 12, color: t.textMuted, fontFamily: font.body }}>Current</span>
                      <span style={{ fontSize: 14, fontWeight: 700, color: t.textPrimary, fontFamily: font.mono }}>${switchConfirm.oldPrice}/mo</span>
                    </div>
                  )}
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                    <span style={{ fontSize: 12, color: t.textMuted, fontFamily: font.body }}>{switchConfirm.isSwitch ? "New" : "Price"}</span>
                    <span style={{ fontSize: 14, fontWeight: 700, color: t.emerald, fontFamily: font.mono }}>${switchConfirm.newPrice}/mo</span>
                  </div>
                  <div style={{ borderTop: `1px solid ${t.border}`, paddingTop: 10 }}>
                    <div style={{ fontSize: 11, color: t.textSecondary, fontFamily: font.body, lineHeight: 1.6 }}>
                      {switchConfirm.isSwitch
                        ? (switchConfirm.isUpgrade
                            ? `You'll be charged a prorated amount of ~$${Math.abs(switchConfirm.newPrice - switchConfirm.oldPrice)} for the remainder of this billing period.`
                            : `You'll receive a credit of ~$${Math.abs(switchConfirm.oldPrice - switchConfirm.newPrice)} on your next invoice.`)
                        : `You'll be redirected to Stripe to enter payment details. You can cancel anytime.`
                      }
                    </div>
                  </div>
                </>
              )}
            </div>

            <div style={{ display: "flex", gap: 10 }}>
              <button onClick={() => setSwitchConfirm(null)} style={{
                flex: 1, padding: "10px 0", borderRadius: 8, border: `1px solid ${t.border}`,
                background: t.surface, color: t.textPrimary, fontSize: 12, fontWeight: 600,
                fontFamily: font.body, cursor: "pointer",
              }}>Cancel</button>
              <button onClick={handleConfirmSwitch} disabled={action === "changing"} style={{
                flex: 1, padding: "10px 0", borderRadius: 8, border: "none",
                background: switchConfirm.plan === "free" ? "#ef4444" : `linear-gradient(135deg, ${t.navyLight}, ${t.navyMid})`,
                color: "#fff", fontSize: 12, fontWeight: 600,
                fontFamily: font.body, cursor: action === "changing" ? "not-allowed" : "pointer",
                opacity: action === "changing" ? 0.7 : 1,
              }}>{action === "changing" ? "Processing..." : switchConfirm.plan === "free" ? "Confirm Downgrade" : (switchConfirm.isSwitch ? `Confirm ${switchConfirm.isUpgrade ? "Upgrade" : "Downgrade"}` : `Subscribe — $${switchConfirm.newPrice}/mo`)}</button>
            </div>
          </div>
        </div>
      , document.body)}
    </div>
  );
}
